import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import useTranslation from "../../i18n/useTranslation";
import {getLocalizedGraphQLError} from "../../util/graphqlError";
import GetUserProfiles from "./queries/GetUserProfiles";
import AddPlayerToTeamSeasonMutation from "./mutations/AddPlayerToTeamSeason";

const ROLE_COACH = 2;

export default function AddTeamMemberModal({
    show,
    onClose,
    role,
    profileId,
    teamId,
    seasonId,
    existingProfileIds = [],
    colors,
    onInvited,
    onCreateNew,
}) {
    const { t } = useTranslation();
    const [getUserProfiles, { data, loading, error }] = GetUserProfiles();
    const [invitePlayer, { loading: isInviting }] = AddPlayerToTeamSeasonMutation();
    const [invitingProfileId, setInvitingProfileId] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
    const [page, setPage] = useState(1);
    const lastSearchTermRef = useRef(null);
    const lastRequestKeyRef = useRef("");
    const pageSize = 10;

    const rawRoleLabel =
        role === ROLE_COACH
            ? t("teamMemberPicker.roles.coach")
            : t("teamMemberPicker.roles.player");

    const roleLabel = rawRoleLabel.charAt(0).toUpperCase() + rawRoleLabel.slice(1);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm.trim());
        }, 300);

        return () => clearTimeout(timer);
    }, [searchTerm]);

    useEffect(() => {
        if (!show || !profileId) return;

        if (lastSearchTermRef.current !== debouncedSearchTerm) {
            lastSearchTermRef.current = debouncedSearchTerm;
            if (page !== 1) {
                setPage(1);
                return;
            }
        }

        const requestKey = `${profileId}:${debouncedSearchTerm}:${page}`;
        if (lastRequestKeyRef.current === requestKey) return;
        lastRequestKeyRef.current = requestKey;

        getUserProfiles({
            variables: {
                q: debouncedSearchTerm || null,
                page,
                pageSize,
            },
            fetchPolicy: "network-only",
        });
    }, [debouncedSearchTerm, getUserProfiles, page, pageSize, show, profileId]);

    useEffect(() => {
        if (!show) {
            setSearchTerm("");
            setDebouncedSearchTerm("");
            setPage(1);
            lastSearchTermRef.current = null;
            lastRequestKeyRef.current = "";
        }
    }, [show]);

    const profiles = useMemo(() => {
        const rawProfiles = Array.isArray(data?.sparePlayers?.objects)
            ? data.sparePlayers.objects
            : Array.isArray(data?.user_profiles)
                ? data.user_profiles
                : Array.isArray(data?.userProfiles?.objects)
                    ? data.userProfiles.objects
                    : [];

        // API can return the same profile more than once; keep a single row per profile id.
        const seenIds = new Set();
        return rawProfiles.filter((profile) => {
            const profileId = Number(profile?.id);
            if (!profileId || seenIds.has(profileId)) return false;
            seenIds.add(profileId);
            return true;
        });
    }, [data]);
    const pagination = useMemo(() => ({
        page: Number(data?.sparePlayers?.page) || page,
        pages: Number(data?.sparePlayers?.pages) || 1,
        hasNext: Boolean(data?.sparePlayers?.hasNext),
        hasPrev: Boolean(data?.sparePlayers?.hasPrev),
    }), [data?.sparePlayers?.hasNext, data?.sparePlayers?.hasPrev, data?.sparePlayers?.page, data?.sparePlayers?.pages, page]);
    const existingIds = useMemo(
        () => new Set((existingProfileIds || []).map((id) => Number(id))),
        [existingProfileIds]
    );

    const handleAdd = async (selectedProfile) => {
        const selectedProfileId = Number(selectedProfile?.id);
        if (!profileId || !teamId || !seasonId || !role) return;

        try {
            setInvitingProfileId(selectedProfileId);
            const { data: response } = await invitePlayer({
                variables: {
                    profileId: Number(selectedProfileId),
                    teamId: Number(teamId),
                    seasonId: Number(seasonId),
                    role: Number(role),
                },
            });

            if (!response?.invitePlayer?.success) {
                toast.error(t("teamMemberPicker.addError"), {
                    autoClose: 3000,
                    position: "top-center",
                    closeOnClick: true,
                });
                return;
            }

            toast.success(t("teamMemberPicker.addSuccess", { role: roleLabel }), {
                autoClose: 2500,
                position: "top-center",
                closeOnClick: true,
            });

            if (onInvited) {
                await onInvited({
                    profileId: selectedProfileId,
                    fullName: selectedProfile?.fullName || "",
                    role: Number(role),
                });
            }
        } catch (err) {
            toast.error(getLocalizedGraphQLError(err, t, "teamMemberPicker.addError"), {
                autoClose: 3000,
                position: "top-center",
                closeOnClick: true,
            });
        } finally {
            setInvitingProfileId(null);
        }
    };

    if (!show) return null;

    return (
        <div
            className="modal d-block"
            role="dialog"
            aria-modal="true"
            style={{ backgroundColor: "rgba(0,0,0,0.45)" }}
        >
            <div className="modal-dialog modal-lg modal-dialog-scrollable kbola-existingMemberDialog" role="document">
                <div className="modal-content kbola-existingMemberModal">
                    <div
                        className="modal-header kbola-existingMemberHeader"
                        style={{
                            backgroundColor: colors?.primary,
                            color: colors?.textOnPrimary,
                        }}
                    >
                        <div className="kbola-existingMemberHeading"><span className="kbola-existingMemberIcon"><i className={role === ROLE_COACH ? "fas fa-clipboard-list" : "fas fa-baseball-ball"} /></span><div><span className="kbola-existingMemberEyebrow">{t("teamMemberPicker.profile")}</span><h5 className="modal-title mb-0">{t("teamMemberPicker.title", { role: roleLabel })}</h5></div></div>
                        <button
                            type="button"
                            className="close"
                            onClick={onClose}
                            aria-label={t("common.cancel")}
                            disabled={isInviting}
                        >
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </div>

                    <div className="modal-body kbola-existingMemberBody">
                        <div className="kbola-existingMemberSearch form-group">
                            <label>{t("teamMemberPicker.searchPlaceholder")}</label><div className="input-group">
                                <div className="input-group-prepend">
                                    <span className="input-group-text" aria-hidden="true">
                                        <i className="fas fa-search" />
                                    </span>
                                </div>
                                <input
                                    type="text"
                                    className="form-control kbola-existingMemberInput"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    placeholder={t("teamMemberPicker.searchPlaceholder")}
                                    disabled={isInviting}
                                />
                            </div>
                        </div>

                        {onCreateNew && <button type="button" className="btn btn-outline-primary btn-block mb-3" onClick={onCreateNew} disabled={isInviting}>
                            <i className="fas fa-user-plus mr-2" />{t("organizations.createUserSubmit")}
                        </button>}

                        {loading ? (
                            <div className="text-center py-3">{t("common.loading")}</div>
                        ) : error ? (
                            <div className="alert alert-danger mb-0">{t("teamMemberPicker.loadError")}</div>
                        ) : !profiles.length ? (
                            <div className="alert alert-light mb-0">
                                {debouncedSearchTerm ? t("teamMemberPicker.noSearchResults") : t("teamMemberPicker.noProfiles")}
                            </div>
                        ) : (
                            <div>
                                <div className="table-responsive kbola-existingMemberTableWrap">
                                    <table className="table mb-0 kbola-existingMemberTable">
                                        <thead>
                                            <tr>
                                                <th>{t("teamMemberPicker.profile")}</th>
                                                <th className="text-right">{t("playersList.actions")}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {profiles.map((profile) => {
                                                const profileKey = Number(profile.id);
                                                const alreadyAdded = existingIds.has(profileKey);
                                                const isRowInviting = invitingProfileId === profileKey;

                                                return (
                                                    <tr key={profile.id}>
                                                        <td><div className="kbola-existingMemberProfile"><span className="kbola-existingMemberAvatar">{(profile.fullName || "?").trim().charAt(0).toUpperCase()}</span><div><strong>{profile.fullName}</strong>{profile.user?.email && <small>{profile.user.email}</small>}</div></div></td>
                                                        <td className="text-right">
                                                            <button
                                                                type="button"
                                                                className="btn btn-kbola btn-sm kbola-existingMemberAddButton"
                                                                    onClick={() => handleAdd(profile)}
                                                                disabled={alreadyAdded || isInviting}
                                                            >
                                                                {isRowInviting
                                                                    ? t("common.loading")
                                                                    : alreadyAdded
                                                                        ? t("teamMemberPicker.alreadyAdded")
                                                                        : t("teamMemberPicker.add")}
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>

                                <div className="d-flex align-items-center justify-content-between mt-3">
                                    <small className="text-muted">
                                        {t("teamMemberPicker.pageInfo", {
                                            page: pagination.page,
                                            pages: pagination.pages,
                                        })}
                                    </small>

                                    <div className="btn-group" role="group" aria-label="pagination">
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary btn-sm"
                                            onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                                            disabled={!pagination.hasPrev || loading || isInviting}
                                        >
                                            {t("common.previous")}
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary btn-sm"
                                            onClick={() => setPage((prev) => prev + 1)}
                                            disabled={!pagination.hasNext || loading || isInviting}
                                        >
                                            {t("common.next")}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="modal-footer kbola-existingMemberFooter">
                        <button
                            type="button"
                            className="btn btn-light"
                            onClick={onClose}
                            disabled={isInviting}
                        >
                            {t("common.cancel")}
                        </button>
                    </div>
                </div>
            </div>
            <style>{`.kbola-existingMemberDialog{max-height:calc(100vh - 2rem)}.kbola-existingMemberModal{border:0;border-radius:18px;overflow:hidden;box-shadow:0 24px 70px rgba(20,21,26,.28)}.kbola-existingMemberHeader{display:flex;align-items:center;padding:23px 27px;background:linear-gradient(135deg,${colors?.primary},${colors?.secondary || colors?.primary});color:${colors?.textOnPrimary};border:0}.kbola-existingMemberHeading{display:flex;align-items:center;gap:13px}.kbola-existingMemberIcon{width:46px;height:46px;border-radius:14px;display:flex;align-items:center;justify-content:center;background:rgba(255,255,255,.2);font-size:18px}.kbola-existingMemberEyebrow{display:block;font-size:10px;font-weight:900;letter-spacing:.12em;text-transform:uppercase;opacity:.75;margin-bottom:3px}.kbola-existingMemberHeader h5{font-size:20px;font-weight:900}.kbola-existingMemberHeader .close{color:inherit;opacity:.9}.kbola-existingMemberBody{padding:25px 27px;background:#f7f9fc;overflow-y:auto}.kbola-existingMemberSearch{padding:16px;border:1px solid #e0e7ef;border-radius:14px;background:#fff;box-shadow:0 4px 12px rgba(20,21,26,.035)}.kbola-existingMemberSearch label{display:block;color:#4b5563;font-size:12px;font-weight:900;margin-bottom:8px}.kbola-existingMemberSearch .input-group-text{border:1px solid #d9e1ea;border-right:0;border-radius:10px 0 0 10px;background:#fbfcfe;color:#8b98a8}.kbola-existingMemberInput{height:42px;border:1px solid #d9e1ea;border-radius:0 10px 10px 0;background:#fbfcfe;font-size:14px}.kbola-existingMemberInput:focus{border-color:#4aa8d2;box-shadow:0 0 0 3px rgba(74,168,210,.13)}.kbola-existingMemberTableWrap{border:1px solid #e0e7ef;border-radius:14px;background:#fff;overflow:hidden}.kbola-existingMemberTable thead{background:#f5f8fb}.kbola-existingMemberTable th{border:0;color:#778395;font-size:11px;font-weight:900;letter-spacing:.06em;text-transform:uppercase;padding:13px 16px}.kbola-existingMemberTable td{padding:13px 16px;border-top:1px solid #edf1f5;vertical-align:middle}.kbola-existingMemberProfile{display:flex;align-items:center;gap:11px}.kbola-existingMemberAvatar{width:36px;height:36px;border-radius:11px;display:flex;align-items:center;justify-content:center;background:#e8f3fa;color:#1687ba;font-size:15px;font-weight:900}.kbola-existingMemberProfile strong{display:block;color:#1d2939;font-size:14px;font-weight:900}.kbola-existingMemberProfile small{display:block;color:#8b98a8;font-size:12px;margin-top:2px}.kbola-existingMemberAddButton{min-width:82px;border-radius:9px;font-weight:900}.kbola-existingMemberFooter{padding:15px 27px;border-top:1px solid #e5eaf0;background:#fff}.kbola-existingMemberFooter .btn-light{border:1px solid #d8e0e8;border-radius:10px;color:#526070;font-weight:800;padding:9px 16px}@media(max-width:700px){.kbola-existingMemberDialog{height:calc(100vh - 1rem);max-height:calc(100vh - 1rem);margin:.5rem auto}.kbola-existingMemberHeader{padding:20px 18px}.kbola-existingMemberBody{padding:18px}.kbola-existingMemberTable th,.kbola-existingMemberTable td{padding:11px 10px}.kbola-existingMemberProfile small{display:none}.kbola-existingMemberAddButton{min-width:70px;padding:7px 9px;font-size:12px}}`}</style>
        </div>
    );
}
